import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { useSelector } from 'react-redux';
import { moreBenefitsInExpensiveTariffs } from 'reactApp/appHelpers/featuresHelpers';
import { aiTariffAbVariantB, aiTariffAbVariantD, aiTariffIdsListCommon } from 'reactApp/appHelpers/featuresHelpers/features/aiTariff';
import { aiRmvPromoQuotaTariffFeatures } from 'reactApp/appHelpers/featuresHelpers/features/imageTools';
import { litresTariffIdsListCommon } from 'reactApp/appHelpers/featuresHelpers/features/litres';
import { familyByOptionsFeature } from 'reactApp/appHelpers/featuresHelpers/features/multiTariff';
import { features } from 'reactApp/constants/products/features';
import type { IFeatureData } from 'reactApp/constants/products/features.types';
import { createQuotaFeaturesListByProductSpace, getQuotaCompactFeatures } from 'reactApp/constants/products/quotaFeatures';
import { templateFeatureIcons } from 'reactApp/constants/products/templateFeatureIcons';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import type { Product } from 'reactApp/types/Billing';
import type { TariffFeatureListItemType } from 'reactApp/ui/TariffCardNew/components/TariffFeatureList.types';
import { isMonth6Period } from 'reactApp/utils/Period';

const { vkui12Cancel } = templateFeatureIcons;

// tempexp_16800-start
type FeaturesBySpace = { variant: 'all' | 'all-active'; shortText: boolean };

export const getFeaturesBySpace = (): FeaturesBySpace | undefined => {
    if (moreBenefitsInExpensiveTariffs === 'c' || moreBenefitsInExpensiveTariffs === 'd') {
        return { shortText: true, variant: 'all' };
    }
    return;
};
// tempexp_16800-end

const getProductFeatures = (
    product: Product,
    compactList: boolean,
    featuresBySpace?: FeaturesBySpace,
    isMultiBasic?: boolean,
    isMultiAdvanced?: boolean,
    isVigboTariff?: boolean,
    isFamilyTariff?: boolean
) => {
    if (product.isBase) {
        return compactList ? features.freeQuotaCompact : features.singleQuotaBase;
    }

    if (product.isCheapest) {
        return features.singleQuotaCheapest;
    }

    if (isVigboTariff) {
        return features.multiTariffVibgo;
    }

    if (isFamilyTariff) {
        return features.multiTariffFamily;
    }

    if (aiTariffAbVariantB && aiTariffIdsListCommon.includes(product.id)) {
        /* tempexp_19645-start */
        return features.aiTariffFeatures;
    }
    if (aiTariffAbVariantD && (isMultiAdvanced || isMultiBasic)) {
        return isMultiAdvanced ? features.aiTariffFeaturesVariantD : features.aiTariffFeaturesBasicVariantD;
    }
    /* tempexp_19645-end */

    if (isMultiBasic) {
        const featuresList = familyByOptionsFeature ? features.familyByOptionsBasic : features.multiTariffBasic;

        // Тарифы до 256гб не поддерживают автозагрузку.
        return product.space.original < SPACE_LIST.gb_256
            ? featuresList.map((item) => {
                  if (item.key === 'unlimit-autoupload') {
                      return {
                          text: 'Безлимит для фото с телефона',
                          key: 'unlimit-autoupload-cancel',
                          icon: vkui12Cancel,
                          disabled: true,
                      };
                  }
                  return item;
              })
            : featuresList;
    }

    if (isMultiAdvanced) {
        const featuresList = familyByOptionsFeature ? features.familyByOptionsAdvanced : features.multiTariffAdvanced;

        const noAutoupload = product.isOnlyFeatures;

        return featuresList.map((item) => {
            if (noAutoupload && item.key === 'unlimit-autoupload') {
                return {
                    text: 'Безлимит для фото с телефона',
                    key: 'unlimit-autoupload-cancel',
                    icon: vkui12Cancel,
                };
            }
            if (aiRmvPromoQuotaTariffFeatures && item.key === 'duplicates') {
                return features.aiRmvTariffFeatures[0];
            }
            return item;
        });
    }

    /* tempexp_17509-start */
    if (litresTariffIdsListCommon.includes(product.id)) {
        return features.litresFeatures;
    }
    /* tempexp_17509-end */

    if (product.isSingleQuota) {
        if (product.isProfessional && product.isDisko) {
            if (isMonth6Period(product.period)) {
                return features.quotaProfessional_6m;
            }
            return features.quotaProfessional;
        }
        // tempexp_16800-start
        if (featuresBySpace) {
            return createQuotaFeaturesListByProductSpace(product, featuresBySpace.variant, featuresBySpace.shortText);
        }
        // tempexp_16800-end
        return compactList ? getQuotaCompactFeatures(product.space) : features.quotaRegular;
    } else if (product.isProfessional) {
        if (isMonth6Period(product.period)) {
            return features.professional_new6M;
        }
        return features.professional_new;
    }
    return features.paidWithIcon;
};

/**
 * Хук для определения какой список фичей будут отображаться в карточке продукта
 * @param product
 */
export const useFeatures = (
    product: Product,
    enableJsxText = true,
    compactList = false,
    featuresBySpace?: FeaturesBySpace,
    isMultiBasic = false,
    isMultiAdvanced = false,
    isVigboTariff = false,
    isFamilyTariff = false
) => {
    const productFeatures = getProductFeatures(
        product,
        compactList,
        featuresBySpace,
        isMultiBasic,
        isMultiAdvanced,
        isVigboTariff,
        isFamilyTariff
    );

    return useFeatureData(productFeatures, enableJsxText);
};

export const useFeatureData = (featureData: IFeatureData[], enableJsxText = true) => {
    const uploadLimit = useSelector(ProductsSelectors.getElasticUploadLimitDefault);

    const { featureList, footerFeatureList } = featureData.reduce(
        (acc, feature) => {
            let text = enableJsxText && feature.textJsx ? feature.textJsx : feature.text;

            if (feature.key === 'quota' || feature.key === 'upload') {
                const limitText = `Загрузка файлов до ${bytesToNDigits(uploadLimit, 3).value}`;
                text = limitText;
            }

            const listItem = {
                key: feature.key,
                text,
                icon: feature.icon,
                onClick: feature.onClick,
                disabled: feature.disabled,
                primaryColor: feature.primaryColor,
                usePropsIcon: feature.usePropsIcon,
                noIcon: feature.noIcon,
                color: feature.color,
            };

            if (feature.delimiters) {
                acc.footerFeatureList.push(listItem);
            } else {
                acc.featureList.push(listItem);
            }

            return acc;
        },
        {
            featureList: <TariffFeatureListItemType[]>[],
            footerFeatureList: <TariffFeatureListItemType[]>[],
        }
    );

    return [featureList, footerFeatureList.length ? footerFeatureList : undefined] as const;
};
